import React from "react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { RefreshCw, Eye, TrendingUp } from "lucide-react";
import { CampaignsCard } from "@/components/CampaignsCard";
import { GoogleReviewLinkService } from "@/components/GoogleReviewLinkService";

const SOURCE_LABELS = {
  oben: "Oben (Hero)",
  mitte: "Mitte (QR-Vorschau)",
  preise: "Preise-Bereich",
  unten: "Unten (Abschluss)",
  unbekannt: "Unbekannt",
};

export const MarketingTab = ({ analytics, fetchAnalytics, hasFeature }) => (
            <div data-testid="marketing-view" className="space-y-6 max-w-5xl">
              <div className="flex items-center justify-between">
                <div>
                  <h2 className="font-[Playfair_Display] text-xl font-semibold text-[var(--brand-ink)]">Landingpage-Performance</h2>
                  <p className="text-sm text-[var(--brand-muted)]">Aufrufe, Demo-Klicks und Klickrate deiner Startseite.</p>
                </div>
                <Button variant="outline" size="sm" onClick={fetchAnalytics} className="border-[var(--brand-border)]" data-testid="marketing-refresh">
                  <RefreshCw className="w-4 h-4 mr-1" /> Aktualisieren
                </Button>
              </div>

              {!analytics ? (
                <div className="text-sm text-[var(--brand-muted)]">Lade Daten …</div>
              ) : (
                <>
                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-4" data-testid="marketing-kpis">
                    <Card className="p-5 border-[var(--brand-border)]">
                      <div className="flex items-center gap-2 text-[var(--brand-muted)] text-xs uppercase tracking-wider mb-1"><Eye className="w-4 h-4" /> Seitenaufrufe</div>
                      <div className="text-3xl font-bold text-[var(--brand-ink)]" data-testid="kpi-views">{analytics.views}</div>
                    </Card>
                    <Card className="p-5 border-[var(--brand-border)]">
                      <div className="flex items-center gap-2 text-[var(--brand-muted)] text-xs uppercase tracking-wider mb-1"><RefreshCw className="w-4 h-4" /> Demo-Klicks</div>
                      <div className="text-3xl font-bold text-[var(--brand-ink)]" data-testid="kpi-clicks">{analytics.clicks}</div>
                    </Card>
                    <Card className="p-5 border-[var(--brand-border)]">
                      <div className="flex items-center gap-2 text-[var(--brand-muted)] text-xs uppercase tracking-wider mb-1"><TrendingUp className="w-4 h-4" /> Klickrate (CTR)</div>
                      <div className="text-3xl font-bold text-[var(--brand-primary)]" data-testid="kpi-ctr">{analytics.ctr}%</div>
                    </Card>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <Card className="p-5 border-[var(--brand-border)]">
                      <div className="text-sm font-semibold text-[var(--brand-ink)] mb-3">Klicks nach Position</div>
                      {Object.keys(analytics.by_source || {}).length === 0 ? (
                        <div className="text-xs text-[var(--brand-muted)]">Noch keine Klicks.</div>
                      ) : (
                        <div className="space-y-2">
                          {Object.entries(analytics.by_source).sort((a, b) => b[1] - a[1]).map(([k, v]) => (
                            <div key={k} className="flex items-center justify-between text-sm">
                              <span className="text-[var(--brand-muted)]">{SOURCE_LABELS[k] || k}</span>
                              <span className="font-semibold text-[var(--brand-ink)]">{v}</span>
                            </div>
                          ))}
                        </div>
                      )}
                    </Card>
                    <Card className="p-5 border-[var(--brand-border)]">
                      <div className="text-sm font-semibold text-[var(--brand-ink)] mb-3">Nach Gerät</div>
                      {Object.keys(analytics.by_device || {}).length === 0 ? (
                        <div className="text-xs text-[var(--brand-muted)]">Noch keine Daten.</div>
                      ) : (
                        <div className="space-y-2">
                          {Object.entries(analytics.by_device).sort((a, b) => b[1] - a[1]).map(([k, v]) => (
                            <div key={k} className="flex items-center justify-between text-sm">
                              <span className="text-[var(--brand-muted)] capitalize">{k === "mobile" ? "Mobil" : k === "desktop" ? "Desktop" : k}</span>
                              <span className="font-semibold text-[var(--brand-ink)]">{v}</span>
                            </div>
                          ))}
                        </div>
                      )}
                    </Card>
                    <Card className="p-5 border-[var(--brand-border)]">
                      <div className="text-sm font-semibold text-[var(--brand-ink)] mb-3">Nach Land</div>
                      {Object.keys(analytics.by_country || {}).length === 0 ? (
                        <div className="text-xs text-[var(--brand-muted)]">Noch keine Daten.</div>
                      ) : (
                        <div className="space-y-2">
                          {Object.entries(analytics.by_country).sort((a, b) => b[1] - a[1]).slice(0, 8).map(([k, v]) => (
                            <div key={k} className="flex items-center justify-between text-sm">
                              <span className="text-[var(--brand-muted)]">{k}</span>
                              <span className="font-semibold text-[var(--brand-ink)]">{v}</span>
                            </div>
                          ))}
                        </div>
                      )}
                    </Card>
                  </div>

                  <Card className="p-5 border-[var(--brand-border)]">
                    <div className="text-sm font-semibold text-[var(--brand-ink)] mb-4">Verlauf (letzte 30 Tage)</div>
                    {(() => {
                      const series = analytics.series || [];
                      const max = Math.max(1, ...series.map((d) => Math.max(d.views, d.clicks)));
                      return (
                        <div className="flex items-end gap-1 h-32" data-testid="marketing-chart">
                          {series.map((d) => (
                            <div key={d.date} className="flex-1 flex flex-col justify-end items-center gap-0.5" title={`${d.date}: ${d.views} Aufrufe, ${d.clicks} Klicks`}>
                              <div className="w-full bg-[var(--brand-border)] rounded-t" style={{ height: `${(d.views / max) * 100}%` }} />
                              <div className="w-full bg-[var(--brand-primary)] rounded-t" style={{ height: `${(d.clicks / max) * 100}%` }} />
                            </div>
                          ))}
                        </div>
                      );
                    })()}
                    <div className="flex items-center gap-4 mt-3 text-xs text-[var(--brand-muted)]">
                      <span className="flex items-center gap-1"><span className="inline-block w-3 h-3 rounded-sm bg-[var(--brand-border)]" /> Aufrufe</span>
                      <span className="flex items-center gap-1"><span className="inline-block w-3 h-3 rounded-sm bg-[var(--brand-primary)]" /> Demo-Klicks</span>
                    </div>
                  </Card>
                </>
              )}

              <GoogleReviewLinkService />

              {hasFeature && hasFeature("crm") && <CampaignsCard />}
            </div>

);
